import React, { useContext, useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  ImageStyle,
  View,
} from "react-native";
import {
  mergeDuplicateImages,
  getDuplicateImagePatients,
  TDuplicateImageCandidate,
  TDuplicatePatient,
} from "../../../../services/api-image-intelligence";
import { SessionContext } from "../../../../contexts";
import {
  NormalText,
  PrimaryButton,
  SecondaryButton,
} from "../../../common/native";

const DEFAULT_PAGE_LIMIT = 10;
const MAX_PAGE_LIMIT = 50;
const MIN_PAGE_LIMIT = 1;

type TDuplicateImageView = TDuplicateImageCandidate & {
  selected: boolean;
};

type TDuplicateGroupView = {
  file_size: number;
  keeperId: string;
  images: TDuplicateImageView[];
};

type TDuplicatePatientView = {
  patient_uuid: string;
  full_name: string;
  duplicate_groups: TDuplicateGroupView[];
  isExpanded: boolean;
};

const formatFileSize = (size: number) => {
  const bytes = Number(size);
  if (Number.isNaN(bytes)) {
    return "Unknown size";
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const transformPatients = (
  patients: TDuplicatePatient[],
): TDuplicatePatientView[] => {
  return patients
    .map((patient) => {
      const groups = (patient.duplicate_groups ?? [])
        .map((group) => {
          const keeperId = group.images?.[0]?.image_id ?? "";
          const images = (group.images ?? []).map<TDuplicateImageView>(
            (image) => ({
              ...image,
              selected: image.image_id !== keeperId,
            }),
          );

          return images.length > 1
            ? {
                file_size: group.file_size ?? 0,
                keeperId,
                images,
              }
            : null;
        })
        .filter((value): value is TDuplicateGroupView => value !== null);

      return {
        patient_uuid: patient.patient_uuid,
        full_name: patient.full_name || "Unnamed patient",
        duplicate_groups: groups,
        isExpanded: false,
      };
    })
    .filter((patient) => patient.duplicate_groups.length > 0);
};

const DuplicateImagesReviewPage: React.FC = () => {
  const session = useContext(SessionContext)?.session;
  const [patients, setPatients] = useState<TDuplicatePatientView[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [mergeLoading, setMergeLoading] = useState<Record<string, boolean>>({});
  const [pageLimit, setPageLimit] = useState(DEFAULT_PAGE_LIMIT);
  const [limitInput, setLimitInput] = useState(String(DEFAULT_PAGE_LIMIT));
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  const fetchPatients = async (cursor?: string, append = false) => {
    if (!session) return;

    if (!append) {
      setIsLoading(true);
    } else {
      setIsFetchingMore(true);
    }

    try {
      const payload = await getDuplicateImagePatients(
        session,
        pageLimit,
        cursor,
      );
      if (!isMountedRef.current) return;

      const nextPatients = transformPatients(payload.patients);
      setPatients((prev) =>
        append ? [...prev, ...nextPatients] : nextPatients,
      );
      setNextCursor(payload.next_cursor ?? null);
      setErrorMessage(null);
    } catch (error: any) {
      if (!isMountedRef.current) return;
      setErrorMessage(
        error?.message ?? "Unable to load duplicate image groups.",
      );
    } finally {
      if (!isMountedRef.current) return;
      setIsLoading(false);
      setIsFetchingMore(false);
    }
  };

  useEffect(() => {
    if (!session) return;
    setPatients([]);
    setNextCursor(null);
    setErrorMessage(null);
    fetchPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, pageLimit]);

  const refreshList = () => {
    if (!session) return;
    setPatients([]);
    setNextCursor(null);
    fetchPatients();
  };

  const handleTogglePatient = (patientId: string) => {
    setPatients((prev) =>
      prev.map((patient) =>
        patient.patient_uuid === patientId
          ? { ...patient, isExpanded: !patient.isExpanded }
          : patient,
      ),
    );
  };

  const updateGroup = (
    patientId: string,
    groupIndex: number,
    updater: (group: TDuplicateGroupView) => TDuplicateGroupView,
  ) => {
    setPatients((prev) =>
      prev.map((patient) => {
        if (patient.patient_uuid !== patientId) {
          return patient;
        }
        const updatedGroups = patient.duplicate_groups.map((group, index) =>
          index === groupIndex ? updater(group) : group,
        );
        return { ...patient, duplicate_groups: updatedGroups };
      }),
    );
  };

  const toggleDuplicateSelection = (
    patientId: string,
    groupIndex: number,
    imageId: string,
  ) => {
    updateGroup(patientId, groupIndex, (group) => ({
      ...group,
      images: group.images.map((image) =>
        image.image_id === imageId
          ? { ...image, selected: !image.selected }
          : image,
      ),
    }));
  };

  const setKeeper = (
    patientId: string,
    groupIndex: number,
    imageId: string,
  ) => {
    updateGroup(patientId, groupIndex, (group) => ({
      ...group,
      keeperId: imageId,
      images: group.images.map((image) => {
        if (image.image_id === imageId) {
          return { ...image, selected: false };
        }
        if (image.image_id === group.keeperId) {
          return { ...image, selected: true };
        }
        return image;
      }),
    }));
  };

  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = setTimeout(() => {
      setShowToast(false);
      setToastMessage(null);
    }, 3500);
  };

  const applyPageLimit = () => {
    const parsedLimit = Number.parseInt(limitInput, 10);
    if (Number.isNaN(parsedLimit)) {
      setErrorMessage("Enter a numeric limit between 1 and 50.");
      return;
    }
    const clampedLimit = Math.min(
      MAX_PAGE_LIMIT,
      Math.max(MIN_PAGE_LIMIT, parsedLimit),
    );
    setErrorMessage(null);
    setPageLimit(clampedLimit);
    if (String(clampedLimit) !== limitInput) {
      setLimitInput(String(clampedLimit));
    }
    showToastMessage(`Loading ${clampedLimit} patient(s) per batch.`);
  };

  const handleDeleteDuplicates = async (
    patientId: string,
    groupIndex: number,
  ) => {
    const patient = patients.find((item) => item.patient_uuid === patientId);
    if (!patient) return;

    const group = patient.duplicate_groups[groupIndex];
    if (!group) return;

    const keeperId = group.keeperId;
    const deleteImageIds = group.images
      .filter((image) => image.image_id !== keeperId && image.selected)
      .map((image) => image.image_id);

    if (!deleteImageIds.length) {
      return;
    }

    const confirmationMessage =
      "This will permanently remove the selected uploads from the clinic’s S3 bucket. Continue?";
    if (typeof window !== "undefined" && !window.confirm(confirmationMessage)) {
      return;
    }

    if (!session) return;
    const mergeKey = `${patientId}-${groupIndex}`;
    setMergeLoading((prev) => ({ ...prev, [mergeKey]: true }));
    try {
      const result = await mergeDuplicateImages(session, {
        patient_uuid: patientId,
        keep_image_id: keeperId,
        delete_image_ids: deleteImageIds,
      });

      showToastMessage(`Removed ${result.deleted} duplicate image(s).`);

      setPatients((prev) =>
        prev
          .map((patientItem) => {
            if (patientItem.patient_uuid !== patientId) {
              return patientItem;
            }
            const updatedGroups = patientItem.duplicate_groups
              .map((existingGroup, index) => {
                if (index !== groupIndex) {
                  return existingGroup;
                }
                const remaining = existingGroup.images.filter(
                  (image) => !deleteImageIds.includes(image.image_id),
                );
                if (remaining.length <= 1) {
                  return null;
                }
                return {
                  ...existingGroup,
                  images: remaining,
                  keeperId,
                };
              })
              .filter((value): value is TDuplicateGroupView => value !== null);

            return {
              ...patientItem,
              duplicate_groups: updatedGroups,
              isExpanded: updatedGroups.length > 0,
            };
          })
          .filter((patientItem) => patientItem.duplicate_groups.length > 0),
      );
    } catch (error: any) {
      setErrorMessage(error?.message ?? "Unable to delete duplicate images.");
    } finally {
      setMergeLoading((prev) => ({ ...prev, [mergeKey]: false }));
    }
  };

  const shouldShowEmptyState =
    patients.length === 0 ||
    patients.every((patient) => patient.duplicate_groups.length === 0);

  if (!session) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#00B7F4" />
      </View>
    );
  }

  return (
    <>
      <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.header}>
          <NormalText
            text="Duplicate Image Review"
            fontWeight="600"
            style={styles.title}
          />
          <NormalText
            text="Delete duplicate images."
            fontWeight="500"
            style={styles.subtitle}
          />
          <View style={styles.controlRow}>
            <SecondaryButton
              text="Refresh patients"
              onPress={refreshList}
              disabled={isLoading}
            />
            <SecondaryButton
              text="Next patients"
              onPress={() => fetchPatients(nextCursor ?? undefined, true)}
              disabled={!nextCursor || isFetchingMore || isLoading}
              loading={isFetchingMore}
              style={styles.nextButton}
            />
          </View>
          <View style={styles.limitRow}>
            <View style={styles.limitLabels}>
              <NormalText
                text="Patients per batch"
                fontWeight="500"
                style={styles.limitLabel}
              />
              <NormalText
                text="Max 50"
                fontWeight="500"
                style={styles.limitHint}
              />
            </View>
            <View style={styles.limitInputRow}>
              <TextInput
                value={limitInput}
                onChangeText={(value) =>
                  setLimitInput(value.replace(/[^0-9]/g, ""))
                }
                keyboardType="number-pad"
                placeholder={String(DEFAULT_PAGE_LIMIT)}
                style={styles.limitInput}
              />
              <SecondaryButton
                text="Apply limit"
                onPress={applyPageLimit}
                disabled={isLoading || isFetchingMore}
                style={styles.limitApplyButton}
              />
            </View>
          </View>
          {errorMessage && (
            <NormalText
              text={errorMessage}
              fontWeight="500"
              style={styles.errorText}
            />
          )}
        </View>

        {isLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#00B7F4" />
          </View>
        ) : shouldShowEmptyState ? (
          <View style={styles.emptyState}>
            <NormalText
              text="No suspected duplicate images for this batch."
              fontWeight="500"
              style={styles.emptyStateText}
            />
          </View>
        ) : (
          patients.map((patient) => (
            <View
              key={patient.patient_uuid}
              style={[
                styles.patientPanel,
                patient.duplicate_groups.length > 0 &&
                  styles.patientPanelActive,
              ]}
            >
              <Pressable
                style={({ pressed }) => [
                  styles.patientHeader,
                  pressed && styles.patientHeaderPressed,
                ]}
                onPress={() => handleTogglePatient(patient.patient_uuid)}
              >
                <View>
                  <NormalText
                    text={patient.full_name}
                    fontWeight="600"
                    style={styles.patientName}
                  />
                  <NormalText
                    text={`${patient.duplicate_groups.length} duplicate group${
                      patient.duplicate_groups.length === 1 ? "" : "s"
                    }`}
                    fontWeight="500"
                    style={styles.patientHint}
                  />
                </View>
                <NormalText
                  text={patient.isExpanded ? "Hide groups" : "Show groups"}
                  fontWeight="500"
                  style={styles.toggleText}
                />
              </Pressable>

              {patient.isExpanded &&
                patient.duplicate_groups.map((group, groupIndex) => {
                  const deleteCount = group.images.filter(
                    (item) => item.image_id !== group.keeperId && item.selected,
                  ).length;
                  const nonKeeperImages = group.images.filter(
                    (item) => item.image_id !== group.keeperId,
                  );
                  const mergeKey = `${patient.patient_uuid}-${groupIndex}`;

                  return (
                    <View
                      key={`${patient.patient_uuid}-${groupIndex}`}
                      style={styles.groupContainer}
                    >
                      <View style={styles.groupHeader}>
                        <NormalText
                          text={`Group ${groupIndex + 1}`}
                          fontWeight="600"
                          style={styles.groupTitle}
                        />
                        <NormalText
                          text={formatFileSize(group.file_size)}
                          fontWeight="500"
                          style={styles.groupHint}
                        />
                      </View>
                      <NormalText
                        text="Duplicates are grouped by exact S3 filesize. Removing one removes the object from storage."
                        fontWeight="400"
                        style={styles.groupDescription}
                      />

                      <View style={styles.imagesGrid}>
                        {group.images.map((image) => {
                          const isKeeper = image.image_id === group.keeperId;
                          return (
                            <View
                              key={image.image_id}
                              style={[
                                styles.imageCard,
                                isKeeper && styles.imageCardKeeper,
                              ]}
                            >
                              <Image
                                source={{ uri: image.image_url || undefined }}
                                style={styles.imageThumbnail as ImageStyle}
                                resizeMode="cover"
                              />
                              {isKeeper && (
                                <View style={styles.keeperBadge}>
                                  <NormalText
                                    text="Keeper"
                                    fontWeight="600"
                                    style={styles.keeperBadgeText}
                                  />
                                </View>
                              )}
                              <View style={styles.imageActions}>
                                <Pressable
                                  onPress={() =>
                                    setKeeper(
                                      patient.patient_uuid,
                                      groupIndex,
                                      image.image_id,
                                    )
                                  }
                                  disabled={isKeeper}
                                  style={({ pressed }) => [
                                    styles.actionButton,
                                    isKeeper && styles.disabledActionButton,
                                    pressed &&
                                      !isKeeper &&
                                      styles.actionButtonPressed,
                                  ]}
                                >
                                  <NormalText
                                    text={
                                      isKeeper
                                        ? "Current keeper"
                                        : "Make keeper"
                                    }
                                    fontWeight="600"
                                    style={[
                                      styles.actionButtonText,
                                      isKeeper && styles.disabledActionText,
                                    ]}
                                  />
                                </Pressable>
                                {!isKeeper && (
                                  <Pressable
                                    onPress={() =>
                                      toggleDuplicateSelection(
                                        patient.patient_uuid,
                                        groupIndex,
                                        image.image_id,
                                      )
                                    }
                                    style={({ pressed }) => [
                                      styles.toggleDeleteButton,
                                      image.selected &&
                                        styles.toggleDeleteButtonSelected,
                                      pressed &&
                                        styles.toggleDeleteButtonPressed,
                                    ]}
                                  >
                                    <NormalText
                                      text={image.selected ? "Delete" : "Keep"}
                                      fontWeight="600"
                                      style={[
                                        styles.toggleDeleteText,
                                        image.selected &&
                                          styles.toggleDeleteTextSelected,
                                      ]}
                                    />
                                  </Pressable>
                                )}
                              </View>
                            </View>
                          );
                        })}
                      </View>

                      <View style={styles.groupFooter}>
                        <NormalText
                          text={`${deleteCount} duplicate(s) selected`}
                          fontWeight="500"
                          style={styles.groupFooterText}
                        />
                        <PrimaryButton
                          text="Delete duplicates"
                          onPress={() =>
                            handleDeleteDuplicates(
                              patient.patient_uuid,
                              groupIndex,
                            )
                          }
                          disabled={
                            deleteCount === 0 || nonKeeperImages.length === 0
                          }
                          loading={mergeLoading[mergeKey]}
                          width={180}
                        />
                      </View>
                    </View>
                  );
                })}
            </View>
          ))
        )}
      </ScrollView>
      {showToast && toastMessage && (
        <View style={styles.toast}>
          <NormalText text={toastMessage} style={styles.toastText} />
        </View>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#F5F6FA",
    padding: 24,
    alignItems: "center",
    minHeight: "100%",
  },
  header: {
    width: "100%",
    maxWidth: 1100,
    marginBottom: 24,
  },
  title: {
    fontSize: 28,
    color: "#0F172A",
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: "#475569",
    marginBottom: 12,
  },
  controlRow: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  limitRow: {
    gap: 8,
    marginBottom: 12,
  },
  limitLabels: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  limitLabel: {
    fontSize: 14,
    color: "#1E293B",
  },
  limitHint: {
    fontSize: 12,
    color: "#64748B",
  },
  limitInputRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  limitInput: {
    width: 120,
    height: 44,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#C4C6CD",
    paddingHorizontal: 12,
    fontSize: 16,
    color: "#1E293B",
    backgroundColor: "#fff",
  },
  limitApplyButton: {
    borderColor: "#0F172A",
  },
  nextButton: {
    borderColor: "#0F172A",
  },
  errorText: {
    color: "#EF4444",
  },
  emptyState: {
    width: "100%",
    maxWidth: 1100,
    padding: 48,
    backgroundColor: "#fff",
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  emptyStateText: {
    color: "#475569",
    fontSize: 18,
  },
  loadingContainer: {
    width: "100%",
    minHeight: 200,
    alignItems: "center",
    justifyContent: "center",
  },
  patientPanel: {
    width: "100%",
    maxWidth: 1100,
    backgroundColor: "#fff",
    borderRadius: 20,
    padding: 18,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    shadowColor: "#0F172A",
    shadowOpacity: 0.05,
    shadowRadius: 15,
    shadowOffset: { width: 0, height: 4 },
  },
  patientPanelActive: {
    borderColor: "#00B7F4",
  },
  patientHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  patientHeaderPressed: {
    opacity: 0.8,
  },
  patientName: {
    fontSize: 20,
    color: "#0F172A",
  },
  patientHint: {
    fontSize: 14,
    color: "#475569",
  },
  toggleText: {
    fontSize: 14,
    color: "#00B7F4",
  },
  groupContainer: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "#E2E8F0",
  },
  groupHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 4,
  },
  groupTitle: {
    fontSize: 16,
    color: "#1E1E2F",
  },
  groupHint: {
    fontSize: 14,
    color: "#475569",
  },
  groupDescription: {
    fontSize: 14,
    color: "#64748B",
    marginBottom: 12,
  },
  imagesGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 12,
  },
  imageCard: {
    width: 200,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    backgroundColor: "#F8FAFC",
    overflow: "hidden",
  },
  imageCardKeeper: {
    borderColor: "#10B981",
  },
  imageThumbnail: {
    width: "100%",
    height: 140,
    backgroundColor: "#d4d9e6",
  },
  keeperBadge: {
    position: "absolute",
    top: 8,
    left: 8,
    backgroundColor: "#fff",
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#10B981",
  },
  keeperBadgeText: {
    fontSize: 12,
    color: "#10B981",
  },
  imageActions: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 10,
  },
  actionButton: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#00B7F4",
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  actionButtonPressed: {
    opacity: 0.8,
  },
  actionButtonText: {
    fontSize: 12,
    color: "#00B7F4",
  },
  disabledActionButton: {
    borderColor: "#E2E8F0",
  },
  disabledActionText: {
    color: "#94A3B8",
  },
  toggleDeleteButton: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  toggleDeleteButtonSelected: {
    borderColor: "#EF4444",
    backgroundColor: "#FEF2F2",
  },
  toggleDeleteButtonPressed: {
    opacity: 0.6,
  },
  toggleDeleteText: {
    fontSize: 12,
    color: "#1F2937",
  },
  toggleDeleteTextSelected: {
    color: "#B91C1C",
  },
  groupFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 12,
  },
  groupFooterText: {
    fontSize: 14,
    color: "#475569",
  },
  toast: {
    position: "absolute",
    bottom: 24,
    right: 24,
    backgroundColor: "#F8FAFC",
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#C4C6CD",
    shadowColor: "#000",
    shadowOpacity: 0.2,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
  },
  toastText: {
    fontSize: 14,
    color: "#0F172A",
  },
});

export default DuplicateImagesReviewPage;
